import { useMemo } from "react";
import {
  useRentals,
  type RentalLead,
  type RentalListing,
} from "@/hooks/use-rentals";

export type PortfolioOption = { id: string; label: string };

/**
 * Property options for every rental listing currently marked "rented".
 * Derived from the listings (not gated by portfolio membership) so a rented
 * unit always appears. The portfolio address is used as the label when known,
 * falling back to the listing title.
 */
export function rentedListingPropertyOptions(
  portfolioProperties: PortfolioOption[],
  listings: RentalListing[],
): PortfolioOption[] {
  const labelByPropertyId = new Map(
    portfolioProperties.map((p) => [p.id, p.label]),
  );
  const seen = new Set<string>();
  const options: PortfolioOption[] = [];
  for (const listing of listings) {
    if (listing.status !== "rented") continue;
    const id = listing.pipeline_property_id;
    if (seen.has(id)) continue;
    seen.add(id);
    options.push({ id, label: labelByPropertyId.get(id) ?? listing.title });
  }
  return options;
}

/** Approved lead tenant name for a pipeline property (matches listing.pipeline_property_id → approved rental_lead). */
export function getApprovedTenantNameForSavedProperty(
  savedPropertyId: string,
  listings: RentalListing[],
  leads: RentalLead[],
): string | null {
  const listingIdsForProperty = new Set(
    listings
      .filter((listing) => listing.pipeline_property_id === savedPropertyId)
      .map((listing) => listing.id),
  );
  const approved = leads.filter(
    (lead) =>
      lead.status === "approved" &&
      // listing_id is NULL on archived applications (listing deleted), which
      // belong to no property's live tenancy.
      lead.listing_id !== null &&
      listingIdsForProperty.has(lead.listing_id),
  );
  return approved[0]?.name ?? null;
}

export function usePortfolioPropertiesWithRentedListing(
  portfolioProperties: PortfolioOption[],
) {
  const { listings, isLoading } = useRentals();

  const eligible = useMemo(
    () => rentedListingPropertyOptions(portfolioProperties, listings),
    [portfolioProperties, listings],
  );

  return { eligiblePortfolioProperties: eligible, isLoading };
}
